import { type FC, useState, useEffect, useMemo, type ReactNode } from 'react';
import { useRefetchableFragment, graphql } from 'react-relay';

import classnames from 'classnames';

import { VideoTile } from 'dibs-elements/exports/VideoTile';

import styles from './ThumbVideo.scss';
import { getVideoImgProps, getVideoFormat } from './helpers';

import { type ThumbVideo_viewer$key } from './__generated__/ThumbVideo_viewer.graphql';

export type ThumbVideoProps = {
    viewer: ThumbVideo_viewer$key;
    itemId: string;
    children: ReactNode;
    poster?: string;
    animateOnMount?: boolean;
};

const viewerFragment = graphql`
    fragment ThumbVideo_viewer on Viewer
    @argumentDefinitions(itemId: { type: "String" })
    @refetchable(queryName: "ThumbVideoRefetchQuery") {
        video: itemVideo(itemId: $itemId) {
            videoThumb: thumbnail(size: w295h166, pad: false)
            height
            width
            fileByVideoSize {
                link
            }
        }
    }
`;

export const ThumbVideo: FC<ThumbVideoProps> = ({
    viewer: viewerRef,
    itemId,
    poster,
    children,
    animateOnMount = false,
}) => {
    const [{ video }, refetch] = useRefetchableFragment(viewerFragment, viewerRef);
    const [hovering, setHovering] = useState(animateOnMount);

    useEffect(() => {
        if (hovering && !video) {
            refetch({ itemId });
        }
    }, [hovering, itemId, refetch, video]);

    const formatStyles = useMemo(() => {
        if (!video) {
            return {};
        }
        const format = getVideoFormat(video);
        return {
            [styles.portraitVideo]: format === 'portrait',
            [styles.landscapeVideo]: format === 'landscape',
        };
    }, [video]);

    const src = video?.fileByVideoSize?.link;
    const classes = classnames(styles.swiperVideo, {
        [styles.hideOverlay]: hovering,
        ...formatStyles,
    });

    if (video && !poster) {
        const imageProps = getVideoImgProps(video);
        poster = imageProps?.src;
    }

    return (
        <div
            className={classes}
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
        >
            {src && <VideoTile animate={hovering} sourceUrl={src} posterOverride={poster} />}
            {children}
        </div>
    );
};
